/**
 * Página EFD-Reinf — Fase 10 (Folha + Funcionários)
 * GridProfile / NeuroSynx Ltda
 *
 * Eventos suportados:
 *   R-1000  Cadastro do Contribuinte
 *   R-2010  Retenção INSS sobre cessão MO / empreitada
 *   R-4010  Retenção IRRF sobre pagamentos a PF (RPA/autônomos)
 *   R-4020  Retenção IRRF sobre pagamentos a PJ
 *   R-2099  Fechamento série R-2xxx
 *   R-4099  Fechamento DIRF (série R-4xxx)
 *
 * Pipeline: Gerar XML → Assinar (cert ICP-Brasil) → Transmitir → Consultar
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatCPF, formatCNPJ } from '../../lib/helpers';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  FileCode, Plus, Download, Trash2, Loader2, Building2, FileSignature,
  Send, ShieldCheck, CheckCircle2, XCircle, Clock, Radio, Users,
  RefreshCw, Edit3,
} from 'lucide-react';
import { useConfirm } from '../../hooks/useConfirm';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TIPOS = {
  'R-1000': { nome: 'Cadastro Contribuinte', cor: 'bg-indigo-50 text-indigo-700 border-indigo-300' },
  'R-2010': { nome: 'Retenção INSS (Cessão MO)', cor: 'bg-blue-50 text-blue-700 border-blue-300' },
  'R-4010': { nome: 'IRRF — PF (RPA)', cor: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  'R-4020': { nome: 'IRRF — PJ', cor: 'bg-teal-50 text-teal-700 border-teal-300' },
  'R-2099': { nome: 'Fechamento R-2xxx', cor: 'bg-amber-50 text-amber-700 border-amber-300' },
  'R-4099': { nome: 'Fechamento R-4xxx (DIRF)', cor: 'bg-purple-50 text-purple-700 border-purple-300' },
};

const STATUS_ICON = {
  rascunho: <Clock className="h-3 w-3 text-slate-500" />,
  assinado: <FileSignature className="h-3 w-3 text-blue-600" />,
  transmitido: <Send className="h-3 w-3 text-amber-600" />,
  processado_ok: <CheckCircle2 className="h-3 w-3 text-emerald-600" />,
  rejeitado: <XCircle className="h-3 w-3 text-red-600" />,
};

const formatDoc = (v) => {
  const d = (v || '').replace(/\D/g, '');
  if (d.length === 11) return formatCPF(d);
  if (d.length === 14) return formatCNPJ(d);
  return d;
};

// ============================================================================
// KPIs
// ============================================================================
const KpiPanel = ({ companyId, refreshKey }) => {
  const [kpis, setKpis] = useState({ total: 0, por_status: {}, por_tipo: {} });
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/api/reinf/kpis`, { ...auth(), params: { company_id: companyId } })
      .then(r => setKpis(r.data)).catch(() => {});
  }, [companyId, refreshKey]);

  const cards = [
    { k: 'Total', v: kpis.total, c: 'bg-slate-50 border-slate-200 text-slate-700' },
    { k: 'Rascunho', v: kpis.por_status?.rascunho || 0, c: 'bg-gray-50 border-gray-200 text-gray-700' },
    { k: 'Assinados', v: kpis.por_status?.assinado || 0, c: 'bg-blue-50 border-blue-200 text-blue-700' },
    { k: 'Transmitidos', v: kpis.por_status?.transmitido || 0, c: 'bg-amber-50 border-amber-200 text-amber-700' },
    { k: 'Processados OK', v: kpis.por_status?.processado_ok || 0, c: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
    { k: 'Rejeitados', v: kpis.por_status?.rejeitado || 0, c: 'bg-red-50 border-red-200 text-red-700' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="reinf-kpis">
      {cards.map(c => (
        <div key={c.k} className={`border rounded-lg p-3 ${c.c}`}>
          <div className="text-xs font-medium opacity-80">{c.k}</div>
          <div className="text-2xl font-bold">{c.v}</div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Tomadores
// ============================================================================
const TomadoresTab = ({ companyId }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ tipo: 'PJ', razao_social: '', cnpj_cpf: '', ativo: true });
  const [loading, setLoading] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await axios.get(`${API}/api/reinf/tomadores`, { ...auth(), params: { company_id: companyId } });
      setList(r.data || []);
    } catch { /* */ }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.razao_social || !form.cnpj_cpf) return toast.error('Razão social e CNPJ/CPF obrigatórios');
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`${API}/api/reinf/tomadores/${editing.id}`, form, auth());
        toast.success('Tomador atualizado');
      } else {
        await axios.post(`${API}/api/reinf/tomadores`, form,
          { ...auth(), params: { company_id: companyId } });
        toast.success('Tomador cadastrado');
      }
      setOpen(false); setEditing(null);
      setForm({ tipo: 'PJ', razao_social: '', cnpj_cpf: '', ativo: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    } finally { setLoading(false); }
  };

  const remove = async (t) => {
    const ok = await confirm({
      title: 'Remover tomador?',
      description: `${t.razao_social} (${t.cnpj_cpf}) será removido.`,
      confirmText: 'Remover',
    });
    if (!ok) return;
    await axios.delete(`${API}/api/reinf/tomadores/${t.id}`, auth());
    toast.success('Removido');
    load();
  };

  const editar = (t) => {
    setEditing(t);
    setForm({ tipo: t.tipo, razao_social: t.razao_social, cnpj_cpf: t.cnpj_cpf, ativo: t.ativo !== false });
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-indigo-600" />Tomadores / Prestadores</CardTitle>
          <CardDescription>Cadastro de PJ/PF prestadoras (base para R-2010 e R-4010/R-4020).</CardDescription>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ tipo: 'PJ', razao_social: '', cnpj_cpf: '', ativo: true }); setOpen(true); }}
                data-testid="reinf-tomador-novo-btn">
          <Plus className="h-4 w-4 mr-1" />Novo
        </Button>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b">
            <tr>
              <th className="py-2">Tipo</th><th>Razão Social</th><th>CNPJ/CPF</th><th>Status</th><th className="w-24">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">Nenhum tomador cadastrado</td></tr>}
            {list.map(t => (
              <tr key={t.id} className="border-b" data-testid={`reinf-tomador-${t.id}`}>
                <td className="py-2"><Badge variant="outline">{t.tipo}</Badge></td>
                <td>{t.razao_social}</td>
                <td className="font-mono text-xs">{formatDoc(t.cnpj_cpf)}</td>
                <td>{t.ativo !== false ? <Badge className="bg-emerald-100 text-emerald-700">Ativo</Badge> : <Badge variant="secondary">Inativo</Badge>}</td>
                <td className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => editar(t)}><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="reinf-tomador-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Tomador' : 'Novo Tomador'}</DialogTitle>
            <DialogDescription>Prestador de serviço (PJ/PF) referenciado em R-2010/R-4010/R-4020.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PJ">PJ — Pessoa Jurídica</SelectItem>
                  <SelectItem value="PF">PF — Pessoa Física</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{form.tipo === 'PJ' ? 'Razão Social' : 'Nome Completo'}</Label>
              <Input value={form.razao_social} onChange={e => setForm({ ...form, razao_social: e.target.value })}
                     data-testid="reinf-tomador-razao-input" />
            </div>
            <div>
              <Label>{form.tipo === 'PJ' ? 'CNPJ (14 dígitos)' : 'CPF (11 dígitos)'}</Label>
              <Input value={formatDoc(form.cnpj_cpf)} onChange={e => setForm({ ...form, cnpj_cpf: e.target.value.replace(/\D/g, '') })}
                     maxLength={form.tipo === 'PJ' ? 18 : 14}
                     data-testid="reinf-tomador-cnpj-input" />
            </div>
            <Button onClick={save} disabled={loading} className="w-full"
                    data-testid="reinf-tomador-save-btn">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Atualizar' : 'Cadastrar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </Card>
  );
};

// ============================================================================
// Gerar Evento
// ============================================================================
const GerarEventoTab = ({ companyId, onGerado }) => {
  const [tipo, setTipo] = useState('R-4099');
  const [tomadores, setTomadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    ambiente: '2',
    per_apur: new Date().toISOString().slice(0, 7),
    ini_valid: new Date().toISOString().slice(0, 7),
    // R-1000
    classif_trib: '99', ind_escr_contabil: '1', ind_des_folha: '0',
    ind_acordo_isenc_mult: '0', ind_situacao_pj: '0',
    nm_ctt: '', cpf_ctt: '', email_ctt: '', cel_ctt: '',
    // R-2010
    cnpj_prestador: '', vr_bruto: '', vr_base_ret: '', vr_ret_prin: '',
    nf_serie: '1', nf_numero: '', nf_dt_emissao: '', nf_obs: '',
    tp_servico_cod: '100000001',
    // R-4010
    cpf_benef: '', nm_benef: '', dt_pgto: '', cod_natureza_rend: '10001',
    vr_rend_bruto: '', vr_rend_tributavel: '', vr_ir_retido: '',
    // R-4020
    cnpj_benef: '', cod_natureza_rend_pj: '15001', vr_pj_bruto: '',
    vr_base_ir: '', vr_ir_retido_pj: '',
    // Fechamentos
    fech_ret: '1', resp_nm: '', resp_cpf: '',
    evt_serv_tom: 'S',
  });

  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/api/reinf/tomadores`, { ...auth(), params: { company_id: companyId } })
      .then(r => setTomadores(r.data || [])).catch(() => {});
  }, [companyId]);

  const _post = async (path, body) => {
    return axios.post(`${API}/api/reinf/${path}/gerar`, body,
      { ...auth(), params: { company_id: companyId } });
  };

  const gerar = async () => {
    if (!companyId) return toast.error('Selecione uma empresa');
    // Validações ANTES de setLoading para não travar o botão
    if (tipo === 'R-1000' && !form.cpf_ctt) return toast.error('CPF do contato obrigatório');
    setLoading(true);
    try {
      let resp;
      if (tipo === 'R-1000') {
        resp = await _post('r1000', {
          ini_valid: form.ini_valid, classif_trib: form.classif_trib,
          ind_escr_contabil: form.ind_escr_contabil, ind_des_folha: form.ind_des_folha,
          ind_acordo_isenc_mult: form.ind_acordo_isenc_mult,
          ind_situacao_pj: form.ind_situacao_pj || null,
          contato: { nm_ctt: form.nm_ctt, cpf_ctt: form.cpf_ctt,
                     email: form.email_ctt, fone_cel: form.cel_ctt },
          ambiente: form.ambiente,
        });
      } else if (tipo === 'R-2010') {
        resp = await _post('r2010', {
          per_apur: form.per_apur,
          cnpj_prestador: form.cnpj_prestador,
          vr_bruto: parseFloat(form.vr_bruto || 0),
          vr_base_ret: parseFloat(form.vr_base_ret || form.vr_bruto || 0),
          vr_ret_prin: parseFloat(form.vr_ret_prin || 0),
          nf_serie: form.nf_serie, nf_numero: form.nf_numero,
          nf_dt_emissao: form.nf_dt_emissao, nf_obs: form.nf_obs || null,
          tp_servico_cod: form.tp_servico_cod, ambiente: form.ambiente,
        });
      } else if (tipo === 'R-4010') {
        resp = await _post('r4010', {
          per_apur: form.per_apur, cpf_benef: form.cpf_benef, nm_benef: form.nm_benef,
          dt_pgto: form.dt_pgto, cod_natureza_rend: form.cod_natureza_rend,
          vr_rend_bruto: parseFloat(form.vr_rend_bruto || 0),
          vr_rend_tributavel: form.vr_rend_tributavel ? parseFloat(form.vr_rend_tributavel) : null,
          vr_ir_retido: form.vr_ir_retido ? parseFloat(form.vr_ir_retido) : null,
          ambiente: form.ambiente,
        });
      } else if (tipo === 'R-4020') {
        resp = await _post('r4020', {
          per_apur: form.per_apur, cnpj_benef: form.cnpj_benef,
          dt_pgto: form.dt_pgto, cod_natureza_rend: form.cod_natureza_rend_pj,
          vr_bruto: parseFloat(form.vr_pj_bruto || 0),
          vr_base_ir: form.vr_base_ir ? parseFloat(form.vr_base_ir) : null,
          vr_ir_retido: form.vr_ir_retido_pj ? parseFloat(form.vr_ir_retido_pj) : null,
          ambiente: form.ambiente,
        });
      } else if (tipo === 'R-2099') {
        resp = await _post('r2099', {
          per_apur: form.per_apur, evt_serv_tom: form.evt_serv_tom,
          resp: form.resp_nm && form.resp_cpf
            ? { nm: form.resp_nm, cpf: form.resp_cpf } : null,
          ambiente: form.ambiente,
        });
      } else if (tipo === 'R-4099') {
        resp = await _post('r4099', {
          per_apur: form.per_apur, fech_ret: form.fech_ret,
          resp: form.resp_nm && form.resp_cpf
            ? { nm: form.resp_nm, cpf: form.resp_cpf } : null,
          ambiente: form.ambiente,
        });
      }
      toast.success(`✅ ${tipo} gerado — ${resp.data.id_reinf.slice(0, 12)}…`);
      if (onGerado) onGerado();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar');
    } finally { setLoading(false); }
  };

  const fillTomador = (id) => {
    const t = tomadores.find(x => x.id === id);
    if (!t) return;
    if (tipo === 'R-2010') setForm({ ...form, cnpj_prestador: t.cnpj_cpf });
    else if (tipo === 'R-4020') setForm({ ...form, cnpj_benef: t.cnpj_cpf });
    else if (tipo === 'R-4010') setForm({ ...form, cpf_benef: t.cnpj_cpf, nm_benef: t.razao_social });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileCode className="h-5 w-5 text-indigo-600" />Gerar Evento EFD-Reinf</CardTitle>
        <CardDescription>Escolha o tipo, preencha os campos e gere o XML. Depois assine e transmita na aba Eventos.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>Tipo de Evento</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="reinf-gerar-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TIPOS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{k} — {v.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Ambiente</Label>
            <Select value={form.ambiente} onValueChange={v => setForm({ ...form, ambiente: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 — Pré-produção</SelectItem>
                <SelectItem value="1">1 — Produção</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {tipo === 'R-1000' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label>Início Validade</Label><Input type="month" value={form.ini_valid} onChange={e => setForm({ ...form, ini_valid: e.target.value })} /></div>
            <div><Label>Class. Tributária</Label><Input value={form.classif_trib} onChange={e => setForm({ ...form, classif_trib: e.target.value })} /></div>
            <div>
              <Label>Escrituração Contábil</Label>
              <Select value={form.ind_escr_contabil} onValueChange={v => setForm({ ...form, ind_escr_contabil: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="0">Não</SelectItem><SelectItem value="1">Sim</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Nome Contato</Label><Input value={form.nm_ctt} onChange={e => setForm({ ...form, nm_ctt: e.target.value })} /></div>
            <div><Label>CPF Contato</Label><Input value={formatCPF(form.cpf_ctt)} onChange={e => setForm({ ...form, cpf_ctt: e.target.value.replace(/\D/g, '') })} maxLength={14} /></div>
            <div><Label>Email</Label><Input value={form.email_ctt} onChange={e => setForm({ ...form, email_ctt: e.target.value })} /></div>
          </div>
        )}

        {tipo === 'R-2010' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label>Período</Label><Input type="month" value={form.per_apur} onChange={e => setForm({ ...form, per_apur: e.target.value })} /></div>
            <div>
              <Label>Tomador (autopreencher)</Label>
              <Select onValueChange={fillTomador}>
                <SelectTrigger data-testid="reinf-r2010-tomador-select"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>{tomadores.filter(t => t.tipo === 'PJ').map(t => <SelectItem key={t.id} value={t.id}>{t.razao_social}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>CNPJ Prestador</Label><Input value={formatCNPJ(form.cnpj_prestador)} onChange={e => setForm({ ...form, cnpj_prestador: e.target.value.replace(/\D/g, '') })} maxLength={18} /></div>
            <div><Label>Valor Bruto</Label><CurrencyInput value={form.vr_bruto} onChange={v => setForm({ ...form, vr_bruto: v, vr_base_ret: v })} /></div>
            <div><Label>Retenção 11% INSS</Label><CurrencyInput value={form.vr_ret_prin} onChange={v => setForm({ ...form, vr_ret_prin: v })} /></div>
            <div><Label>Cód. Serviço (9d)</Label><Input value={form.tp_servico_cod} onChange={e => setForm({ ...form, tp_servico_cod: e.target.value })} /></div>
            <div><Label>NF Série</Label><Input value={form.nf_serie} onChange={e => setForm({ ...form, nf_serie: e.target.value })} /></div>
            <div><Label>NF Número</Label><Input value={form.nf_numero} onChange={e => setForm({ ...form, nf_numero: e.target.value })} /></div>
            <div><Label>NF Data Emissão</Label><Input type="date" value={form.nf_dt_emissao} onChange={e => setForm({ ...form, nf_dt_emissao: e.target.value })} /></div>
            <div className="col-span-3"><Label>Obs</Label><Textarea rows={2} value={form.nf_obs} onChange={e => setForm({ ...form, nf_obs: e.target.value })} /></div>
          </div>
        )}

        {tipo === 'R-4010' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label>Período</Label><Input type="month" value={form.per_apur} onChange={e => setForm({ ...form, per_apur: e.target.value })} /></div>
            <div><Label>CPF Beneficiário</Label><Input value={formatCPF(form.cpf_benef)} onChange={e => setForm({ ...form, cpf_benef: e.target.value.replace(/\D/g, '') })} maxLength={14} /></div>
            <div><Label>Nome</Label><Input value={form.nm_benef} onChange={e => setForm({ ...form, nm_benef: e.target.value })} /></div>
            <div><Label>Data Pagamento</Label><Input type="date" value={form.dt_pgto} onChange={e => setForm({ ...form, dt_pgto: e.target.value })} /></div>
            <div><Label>Cód. Natureza (5d)</Label><Input value={form.cod_natureza_rend} onChange={e => setForm({ ...form, cod_natureza_rend: e.target.value })} /></div>
            <div><Label>Rend. Bruto</Label><CurrencyInput value={form.vr_rend_bruto} onChange={v => setForm({ ...form, vr_rend_bruto: v, vr_rend_tributavel: v })} /></div>
            <div><Label>Rend. Tributável</Label><CurrencyInput value={form.vr_rend_tributavel} onChange={v => setForm({ ...form, vr_rend_tributavel: v })} /></div>
            <div><Label>IRRF Retido</Label><CurrencyInput value={form.vr_ir_retido} onChange={v => setForm({ ...form, vr_ir_retido: v })} /></div>
          </div>
        )}

        {tipo === 'R-4020' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label>Período</Label><Input type="month" value={form.per_apur} onChange={e => setForm({ ...form, per_apur: e.target.value })} /></div>
            <div>
              <Label>Tomador PJ</Label>
              <Select onValueChange={fillTomador}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>{tomadores.filter(t => t.tipo === 'PJ').map(t => <SelectItem key={t.id} value={t.id}>{t.razao_social}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>CNPJ Beneficiário</Label><Input value={formatCNPJ(form.cnpj_benef)} onChange={e => setForm({ ...form, cnpj_benef: e.target.value.replace(/\D/g, '') })} maxLength={18} /></div>
            <div><Label>Data Pagamento</Label><Input type="date" value={form.dt_pgto} onChange={e => setForm({ ...form, dt_pgto: e.target.value })} /></div>
            <div><Label>Cód. Natureza</Label><Input value={form.cod_natureza_rend_pj} onChange={e => setForm({ ...form, cod_natureza_rend_pj: e.target.value })} /></div>
            <div><Label>Valor Bruto</Label><CurrencyInput value={form.vr_pj_bruto} onChange={v => setForm({ ...form, vr_pj_bruto: v, vr_base_ir: v })} /></div>
            <div><Label>Base IR</Label><CurrencyInput value={form.vr_base_ir} onChange={v => setForm({ ...form, vr_base_ir: v })} /></div>
            <div><Label>IRRF Retido</Label><CurrencyInput value={form.vr_ir_retido_pj} onChange={v => setForm({ ...form, vr_ir_retido_pj: v })} /></div>
          </div>
        )}

        {tipo === 'R-2099' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label>Período</Label><Input type="month" value={form.per_apur} onChange={e => setForm({ ...form, per_apur: e.target.value })} /></div>
            <div>
              <Label>Houve Tomador?</Label>
              <Select value={form.evt_serv_tom} onValueChange={v => setForm({ ...form, evt_serv_tom: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="S">Sim</SelectItem><SelectItem value="N">Não</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Resp. Nome</Label><Input value={form.resp_nm} onChange={e => setForm({ ...form, resp_nm: e.target.value })} /></div>
            <div><Label>Resp. CPF</Label><Input value={formatCPF(form.resp_cpf)} onChange={e => setForm({ ...form, resp_cpf: e.target.value.replace(/\D/g, '') })} maxLength={14} /></div>
          </div>
        )}

        {tipo === 'R-4099' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label>Período</Label><Input type="month" value={form.per_apur} onChange={e => setForm({ ...form, per_apur: e.target.value })} /></div>
            <div>
              <Label>Ação</Label>
              <Select value={form.fech_ret} onValueChange={v => setForm({ ...form, fech_ret: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">Fechamento</SelectItem><SelectItem value="0">Reabertura</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Resp. Nome</Label><Input value={form.resp_nm} onChange={e => setForm({ ...form, resp_nm: e.target.value })} /></div>
            <div><Label>Resp. CPF</Label><Input value={formatCPF(form.resp_cpf)} onChange={e => setForm({ ...form, resp_cpf: e.target.value.replace(/\D/g, '') })} maxLength={14} /></div>
          </div>
        )}

        <Button onClick={gerar} disabled={loading} data-testid="reinf-gerar-btn" className="w-full md:w-auto">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          <Plus className="h-4 w-4 mr-1" />Gerar XML
        </Button>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Eventos Gerados
// ============================================================================
const EventosTab = ({ companyId, refreshKey, onChange }) => {
  const [eventos, setEventos] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [busy, setBusy] = useState(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    if (!companyId) return;
    const params = { company_id: companyId, limit: 200 };
    if (filtroTipo !== 'todos') params.tipo = filtroTipo;
    if (filtroStatus !== 'todos') params.status = filtroStatus;
    const r = await axios.get(`${API}/api/reinf/eventos`, { ...auth(), params });
    setEventos(r.data || []);
  }, [companyId, filtroTipo, filtroStatus]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const acao = async (ev, op) => {
    setBusy(ev.id);
    try {
      if (op === 'assinar') {
        await axios.post(`${API}/api/reinf/eventos/${ev.id}/assinar`, {}, auth());
        toast.success('Assinado');
      } else if (op === 'transmitir') {
        const r = await axios.post(`${API}/api/reinf/eventos/${ev.id}/transmitir`, {}, auth());
        toast.success(`Transmitido — ${r.data.resposta?.protocolo || ''}`);
      } else if (op === 'consultar') {
        const r = await axios.post(`${API}/api/reinf/eventos/${ev.id}/consultar`, {}, auth());
        toast.success(`${r.data.status}`);
      } else if (op === 'excluir') {
        const ok = await confirm({ title: 'Excluir evento?', description: `${ev.tipo} ${ev.id_reinf?.slice(0, 12)}…` });
        if (!ok) return;
        await axios.delete(`${API}/api/reinf/eventos/${ev.id}`, auth());
        toast.success('Removido');
      } else if (op === 'xml') {
        const r = await axios.get(`${API}/api/reinf/eventos/${ev.id}/xml`, auth());
        const blob = new Blob([r.data], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${ev.tipo}_${ev.id_reinf}.xml`;
        a.click(); URL.revokeObjectURL(url);
      }
      load(); if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro');
    } finally { setBusy(null); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileCode className="h-5 w-5 text-indigo-600" />Eventos Gerados
          <Button size="sm" variant="ghost" onClick={load} className="ml-auto"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </CardTitle>
        <div className="flex gap-2 mt-2">
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger className="w-44" data-testid="reinf-filtro-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os tipos</SelectItem>
              {Object.keys(TIPOS).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filtroStatus} onValueChange={setFiltroStatus}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              <SelectItem value="rascunho">Rascunho</SelectItem>
              <SelectItem value="assinado">Assinado</SelectItem>
              <SelectItem value="transmitido">Transmitido</SelectItem>
              <SelectItem value="processado_ok">Processado OK</SelectItem>
              <SelectItem value="rejeitado">Rejeitado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b">
            <tr><th className="py-2">Tipo</th><th>ID</th><th>Período</th><th>Status</th><th>Criado</th><th className="w-72">Ações</th></tr>
          </thead>
          <tbody>
            {eventos.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">Nenhum evento</td></tr>}
            {eventos.map(ev => (
              <tr key={ev.id} className="border-b" data-testid={`reinf-evento-${ev.id}`}>
                <td className="py-2"><Badge variant="outline" className={TIPOS[ev.tipo]?.cor}>{ev.tipo}</Badge></td>
                <td className="font-mono text-xs">{ev.id_reinf?.slice(0, 14)}…</td>
                <td>{ev.payload?.per_apur || ev.payload?.ini_valid || '-'}</td>
                <td className="flex items-center gap-1">{STATUS_ICON[ev.status]}<span className="text-xs">{ev.status}</span></td>
                <td className="text-xs text-slate-500">{(ev.criado_em || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="flex flex-wrap gap-1">
                  <Button size="sm" variant="ghost" disabled={busy === ev.id} onClick={() => acao(ev, 'xml')} title="Baixar XML">
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  {ev.status === 'rascunho' && (
                    <Button size="sm" variant="ghost" disabled={busy === ev.id} onClick={() => acao(ev, 'assinar')} title="Assinar"
                            data-testid={`reinf-assinar-${ev.id}`}>
                      <FileSignature className="h-3.5 w-3.5 text-blue-600" />
                    </Button>
                  )}
                  {ev.status === 'assinado' && (
                    <Button size="sm" variant="ghost" disabled={busy === ev.id} onClick={() => acao(ev, 'transmitir')} title="Transmitir">
                      <Send className="h-3.5 w-3.5 text-amber-600" />
                    </Button>
                  )}
                  {ev.status === 'transmitido' && (
                    <Button size="sm" variant="ghost" disabled={busy === ev.id} onClick={() => acao(ev, 'consultar')} title="Consultar">
                      <Radio className="h-3.5 w-3.5 text-emerald-600" />
                    </Button>
                  )}
                  {(ev.status === 'rascunho' || ev.status === 'rejeitado') && (
                    <Button size="sm" variant="ghost" disabled={busy === ev.id} onClick={() => acao(ev, 'excluir')} title="Excluir">
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      <ConfirmDialog />
    </Card>
  );
};

// ============================================================================
// Transmissão / Status
// ============================================================================
const TransmissaoTab = () => {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${API}/api/reinf/transmissao/status`, auth()).then(r => setS(r.data)); }, []);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Radio className="h-5 w-5 text-indigo-600" />Status da Transmissão</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge className={s.modo === 'real' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
            MODO {s.modo?.toUpperCase()}
          </Badge>
          {s.modo === 'mock' && <span className="text-xs text-slate-500">Defina REINF_TRANSMISSAO_MODO=real para enviar à Receita Federal.</span>}
        </div>
        <div className="text-sm">
          <div className="font-medium">Certificado A1 Ativo:</div>
          {s.certificado_ativo ? (
            <div className="mt-1 text-slate-700">
              <ShieldCheck className="inline h-4 w-4 text-emerald-600 mr-1" />
              {s.certificado_ativo.titular} <Badge variant="outline" className="ml-2">{s.certificado_ativo.tipo}</Badge>
            </div>
          ) : (
            <div className="text-red-600 text-sm">Nenhum certificado ativo. Cadastre o e-CNPJ ICP-Brasil em Cadastro de Empresas.</div>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-2">
          <div>Endpoint Pré-Produção: <span className="font-mono">{s.endpoints?.homologacao_envio}</span></div>
          <div>Endpoint Produção: <span className="font-mono">{s.endpoints?.producao_envio}</span></div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Página
// ============================================================================
export default function ReinfPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [tab, setTab] = useState('eventos');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    axios.get(`${API}/api/companies`, auth()).then(r => {
      setCompanies(r.data || []);
      if (r.data?.length) setCompanyId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const refresh = () => setRefreshKey(k => k + 1);

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="reinf-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileCode className="h-7 w-7 text-indigo-600" />
            EFD-Reinf <Badge variant="outline" className="ml-2">Fase 10</Badge>
          </h1>
          <p className="text-sm text-slate-500">R-1000 · R-2010 · R-4010 · R-4020 · R-2099 · R-4099</p>
        </div>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-slate-500" />
          <Select value={companyId || ''} onValueChange={setCompanyId}>
            <SelectTrigger className="w-72" data-testid="reinf-company-select"><SelectValue placeholder="Selecione a empresa" /></SelectTrigger>
            <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name || c.razao_social}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <KpiPanel companyId={companyId} refreshKey={refreshKey} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="eventos" data-testid="reinf-tab-eventos">Eventos</TabsTrigger>
          <TabsTrigger value="gerar" data-testid="reinf-tab-gerar">Gerar</TabsTrigger>
          <TabsTrigger value="tomadores" data-testid="reinf-tab-tomadores">Tomadores</TabsTrigger>
          <TabsTrigger value="transmissao" data-testid="reinf-tab-transmissao">Transmissão</TabsTrigger>
        </TabsList>

        <TabsContent value="eventos" className="mt-4">
          <EventosTab companyId={companyId} refreshKey={refreshKey} onChange={refresh} />
        </TabsContent>
        <TabsContent value="gerar" className="mt-4">
          <GerarEventoTab companyId={companyId} onGerado={refresh} />
        </TabsContent>
        <TabsContent value="tomadores" className="mt-4">
          <TomadoresTab companyId={companyId} />
        </TabsContent>
        <TabsContent value="transmissao" className="mt-4">
          <TransmissaoTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
